import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Users,
  Search,
  Plus,
  Ban,
  RotateCcw,
  Trash2,
  UserCheck,
  Edit,
  Filter,
  Download,
  MoreHorizontal,
  AlertTriangle,
  Crown,
  ShieldCheck,
  User,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  CreditCard,
  DollarSign,
  Key,
  Copy,
  Check
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { adminApi } from '@/lib/api'

interface User {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  firebaseUid: string | null
  credits: number
  lastActive: string
  role: 'User' | 'Editor' | 'Admin'
  timezone: string | undefined
  globallyUnsubscribed: boolean
  preferredModel: string | null
  plan: string | null
  status: 'PENDING_ACTIVATION' | 'ACTIVE' | 'SUSPENDED'
  inviteQuota: number
  referralCode: string | null
  signupReferralId: string | null
  featureFlags: Record<string, any> | null
  createdAt: string
  updatedAt: string
  // Credit information
  creditStats?: {
    totalCreditsUsed: number
    creditsRemaining: number
    percentageUsed: number
    estimatedCost: number
    actualCost: number
  }
  currentCredits?: number
  usedCredits?: number
  estimatedCost?: number
}

interface UserStats {
  totalUsers: number
  activeUsers: number
  pendingUsers: number
  suspendedUsers: number
  totalCredits: number
}

function UsersPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<User[]>([])
  const [userStats, _setUserStats] = useState<UserStats | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isSuspendDialogOpen, setIsSuspendDialogOpen] = useState(false)
  const [suspendReason, setSuspendReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [isApiTokenDialogOpen, setIsApiTokenDialogOpen] = useState(false)
  const [apiTokenName, setApiTokenName] = useState('')
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [tokenCopied, setTokenCopied] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'User',
    plan: '',
    preferredModel: '',
    timezone: '',
    inviteQuota: 5,
    featureFlags: {} as Record<string, any>
  })

  // Load users from API
  const loadUsers = async () => {
    try {
      setLoading(true)
      
      // Use the new credit-enhanced users endpoint if available, fallback to regular users endpoint
      let usersResponse;
      try {
        usersResponse = await adminApi.getUsersWithCredits({ limit: 100 })
      } catch (error) {
        console.log('Credit-enhanced endpoint not available, falling back to regular users endpoint')
        usersResponse = await adminApi.getUsers({
          search: searchTerm || undefined,
          role: roleFilter !== 'all' ? roleFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: 100
        })
      }
      
      // getUserStats endpoint not implemented yet, commenting out for now
      // const statsResponse = await adminApi.getUserStats().catch(() => null)
      
      setUsers(usersResponse.users || usersResponse || [])
      // if (statsResponse) {
      //   _setUserStats(statsResponse)
      // }
    } catch (error) {
      console.error('Failed to load users:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [searchTerm, roleFilter, statusFilter])

  // Update existing user
  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser) return

    try {
      setActionLoading(true)
      console.log('Updating user with formData:', formData)
      await adminApi.updateUser(selectedUser.id, formData)
      setIsEditDialogOpen(false)
      setSelectedUser(null)
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'User',
        plan: '',
        preferredModel: '',
        timezone: '',
        inviteQuota: 5,
        featureFlags: {}
      })
      await loadUsers()
    } catch (error) {
      console.error('Failed to update user:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Create new user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setActionLoading(true)
      await adminApi.createUser(formData)
      setIsCreateDialogOpen(false)
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'User',
        plan: '',
        preferredModel: '',
        timezone: '',
        inviteQuota: 5,
        featureFlags: {}
      })
      await loadUsers()
    } catch (error) {
      console.error('Failed to create user:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Update user status
  const handleUpdateStatus = async (userId: string, status: string) => {
    try {
      setActionLoading(true)
      await adminApi.updateUserStatus(userId, status)
      await loadUsers()
    } catch (error) {
      console.error('Failed to update user status:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Suspend user
  const handleSuspendUser = async () => {
    if (!selectedUser || !suspendReason.trim()) return
    
    try {
      setActionLoading(true)
      await adminApi.suspendUser(selectedUser.id, suspendReason)
      setIsSuspendDialogOpen(false)
      setSuspendReason('')
      setSelectedUser(null)
      await loadUsers()
    } catch (error) {
      console.error('Failed to suspend user:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      setActionLoading(true)
      await adminApi.deleteUser(selectedUser.id)
      setIsDeleteDialogOpen(false)
      setSelectedUser(null)
      await loadUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleGenerateApiToken = async () => {
    if (!selectedUser || !apiTokenName.trim()) return

    try {
      setActionLoading(true)
      const response = await adminApi.createApiToken(selectedUser.id, apiTokenName.trim())
      setGeneratedToken(response.token)
      setApiTokenName('')
    } catch (error) {
      console.error('Failed to generate API token:', error)
      alert('Failed to generate API token. Please try again.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCopyToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 2000)
    }
  }

  const handleCloseTokenDialog = () => {
    setIsApiTokenDialogOpen(false)
    setGeneratedToken(null)
    setApiTokenName('')
    setTokenCopied(false)
    setSelectedUser(null)
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        )
      case 'PENDING_ACTIVATION':
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        )
      case 'SUSPENDED':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Suspended
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  // Get role badge
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'Admin':
        return (
          <Badge variant="default" className="bg-purple-100 text-purple-800">
            <Crown className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        )
      case 'Editor':
        return (
          <Badge variant="outline" className="border-blue-300 text-blue-800">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Editor
          </Badge>
        )
      case 'User':
        return (
          <Badge variant="secondary">
            <User className="h-3 w-3 mr-1" />
            User
          </Badge>
        )
      default:
        return <Badge variant="outline">{role}</Badge>
    }
  }

  // Format display name
  const getDisplayName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.email.split('@')[0]
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground">
            Manage user accounts, permissions, and activity across the platform.
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new user to the platform with specified role and settings.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser}>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="User">User</SelectItem>
                        <SelectItem value="Editor">Editor</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="plan">Plan</Label>
                    <Input
                      id="plan"
                      value={formData.plan}
                      onChange={(e) => setFormData({...formData, plan: e.target.value})}
                      placeholder="free, pro, enterprise"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inviteQuota">Invite Quota</Label>
                    <Input
                      id="inviteQuota"
                      type="number"
                      value={formData.inviteQuota}
                      onChange={(e) => setFormData({...formData, inviteQuota: parseInt(e.target.value) || 0})}
                      placeholder="Number of invites"
                      min="0"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={actionLoading}>
                    {actionLoading ? 'Creating...' : 'Create User'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit User Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
                <DialogDescription>
                  Update user information, role, and settings.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleEditUser}>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email *</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-firstName">First Name</Label>
                      <Input
                        id="edit-firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-lastName">Last Name</Label>
                      <Input
                        id="edit-lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="User">User</SelectItem>
                        <SelectItem value="Editor">Editor</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-plan">Plan</Label>
                    <Input
                      id="edit-plan"
                      value={formData.plan}
                      onChange={(e) => setFormData({...formData, plan: e.target.value})}
                      placeholder="free, pro, enterprise"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-timezone">Timezone</Label>
                    <Input
                      id="edit-timezone"
                      value={formData.timezone}
                      onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                      placeholder="America/New_York"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-inviteQuota">Invite Quota</Label>
                    <Input
                      id="edit-inviteQuota"
                      type="number"
                      value={formData.inviteQuota}
                      onChange={(e) => setFormData({...formData, inviteQuota: parseInt(e.target.value) || 0})}
                      placeholder="Number of invites"
                      min="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Feature Flags</Label>
                    <div className="space-y-3 p-3 border rounded-md">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="flag-cacheDebug" className="text-sm font-normal">
                          Enable Cache Debug
                        </Label>
                        <Switch
                          id="flag-cacheDebug"
                          checked={formData.featureFlags?.cacheDebug || false}
                          onCheckedChange={(checked) => 
                            setFormData({
                              ...formData, 
                              featureFlags: {
                                ...formData.featureFlags,
                                cacheDebug: checked
                              }
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-featureFlags" className="text-sm">
                          Custom Flags (JSON)
                        </Label>
                        <Textarea
                          id="edit-featureFlags"
                          value={JSON.stringify(formData.featureFlags, null, 2)}
                          onChange={(e) => {
                            try {
                              const flags = JSON.parse(e.target.value)
                              setFormData({...formData, featureFlags: flags})
                            } catch {
                              // Invalid JSON, ignore
                            }
                          }}
                          placeholder='{"flag1": true, "flag2": "value"}'
                          className="font-mono text-xs"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground">
                          Edit feature flags as JSON. Be careful with syntax!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={actionLoading}>
                    {actionLoading ? 'Updating...' : 'Update User'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {userStats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats.totalUsers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats.activeUsers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats.pendingUsers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suspended</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats.suspendedUsers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats.totalCredits.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="Admin">Admin</SelectItem>
            <SelectItem value="Editor">Editor</SelectItem>
            <SelectItem value="User">User</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="PENDING_ACTIVATION">Pending</SelectItem>
            <SelectItem value="SUSPENDED">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          More Filters
        </Button>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({users.length})</CardTitle>
          <CardDescription>
            Manage user accounts, roles, and permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Current Credits</TableHead>
                <TableHead>Est. Cost</TableHead>
                <TableHead>Invites</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{getDisplayName(user)}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                      {user.globallyUnsubscribed && (
                        <Badge variant="outline" className="text-xs">Unsubscribed</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <div className="space-y-1">
                        <div className="font-medium">
                          {(user.currentCredits || user.credits || 0).toLocaleString()}
                        </div>
                        {user.usedCredits !== undefined && (
                          <div className="text-xs text-muted-foreground">
                            {user.usedCredits.toLocaleString()} used
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <div className="font-medium">
                        {user.estimatedCost !== undefined && user.estimatedCost !== null
                          ? `$${Number(user.estimatedCost).toFixed(4)}`
                          : '-'
                        }
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{user.inviteQuota || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    {user.plan ? (
                      <Badge variant="outline">{user.plan}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem 
                          onClick={() => navigate(`/users/${user.id}`, { 
                            state: { from: '/users', direction: 'push' }
                          })}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user)
                            setFormData({
                              email: user.email,
                              firstName: user.firstName || '',
                              lastName: user.lastName || '',
                              role: user.role,
                              plan: user.plan || '',
                              preferredModel: user.preferredModel || '',
                              timezone: user.timezone || '',
                              inviteQuota: user.inviteQuota || 0,
                              featureFlags: user.featureFlags || {}
                            })
                            setIsEditDialogOpen(true)
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user)
                            setIsApiTokenDialogOpen(true)
                          }}
                        >
                          <Key className="h-4 w-4 mr-2" />
                          Generate API Token
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user.status === 'PENDING_ACTIVATION' && (
                          <DropdownMenuItem 
                            onClick={() => handleUpdateStatus(user.id, 'ACTIVE')}
                            disabled={actionLoading}
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Activate
                          </DropdownMenuItem>
                        )}
                        {user.status === 'ACTIVE' && (
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedUser(user)
                              setIsSuspendDialogOpen(true)
                            }}
                            className="text-yellow-600"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Suspend
                          </DropdownMenuItem>
                        )}
                        {user.status === 'SUSPENDED' && (
                          <DropdownMenuItem 
                            onClick={() => handleUpdateStatus(user.id, 'ACTIVE')}
                            disabled={actionLoading}
                            className="text-green-600"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reactivate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => {
                            setSelectedUser(user)
                            setIsDeleteDialogOpen(true)
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {users.length === 0 && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No users found</h3>
              <p className="text-muted-foreground">
                {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your search terms or filters.'
                  : 'No users have been created yet.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suspend User Dialog */}
      <Dialog open={isSuspendDialogOpen} onOpenChange={setIsSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
            <DialogDescription>
              Suspend "{selectedUser?.email}". Please provide a reason for suspension.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="suspendReason">Reason for suspension *</Label>
              <Input
                id="suspendReason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="e.g., Policy violation, inappropriate behavior"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSuspendDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleSuspendUser}
              disabled={actionLoading || !suspendReason.trim()}
            >
              {actionLoading ? 'Suspending...' : 'Suspend User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedUser?.email}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">
              This will permanently delete the user and all associated data.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={actionLoading}
            >
              {actionLoading ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Token Generation Dialog */}
      <Dialog open={isApiTokenDialogOpen} onOpenChange={handleCloseTokenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {generatedToken ? 'API Token Generated' : 'Generate API Token'}
            </DialogTitle>
            <DialogDescription>
              {generatedToken
                ? 'Copy this token now - you won\'t be able to see it again!'
                : `Create an API token for ${selectedUser?.email || 'user'}`}
            </DialogDescription>
          </DialogHeader>

          {!generatedToken ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="tokenName">Token Name</Label>
                <Input
                  id="tokenName"
                  value={apiTokenName}
                  onChange={(e) => setApiTokenName(e.target.value)}
                  placeholder="e.g., Production API Access"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  A descriptive name to help identify this token
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Save this token now!</p>
                    <p>This is the only time you'll be able to see it. Store it securely.</p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <Label>API Token</Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    value={generatedToken}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyToken}
                  >
                    {tokenCopied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {tokenCopied && (
                  <p className="text-sm text-green-600 mt-1">Copied to clipboard!</p>
                )}
              </div>

              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-700">
                  <strong>Usage:</strong> Include this token in the Authorization header:
                </p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded mt-2 block">
                  Authorization: Bearer {generatedToken}
                </code>
              </div>
            </div>
          )}

          <DialogFooter>
            {!generatedToken ? (
              <>
                <Button variant="outline" onClick={handleCloseTokenDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateApiToken}
                  disabled={actionLoading || !apiTokenName.trim()}
                >
                  {actionLoading ? 'Generating...' : 'Generate Token'}
                </Button>
              </>
            ) : (
              <Button onClick={handleCloseTokenDialog} className="w-full">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export { UsersPage }
export default UsersPage